import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  AtSign,
  Bell,
  Clock,
  Loader2,
  Plus,
  Save,
  X,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../../components/Input/Input";
import Notification from "../../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../../../apiService";
import { getErrorMessage } from "../../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./HelpDeskConfig.module.scss";

const BACK_PATH = "/admin/help-desk";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const EmailChipsField = ({ label, value, onChange, error, placeholder }) => {
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState("");

  const addEmail = () => {
    const next = draft.trim();
    if (!next) return;
    if (!EMAIL_REGEX.test(next)) {
      setLocalError("Enter a valid email address");
      return;
    }
    if (value.includes(next)) {
      setLocalError("Email already added");
      return;
    }
    onChange([...value, next]);
    setDraft("");
    setLocalError("");
  };

  const removeEmail = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <div className={styles["helpdesk-config__chips-field"]}>
      <label className={styles["helpdesk-config__field-label"]}>{label}</label>

      <div className={styles["helpdesk-config__chip-input-row"]}>
        <input
          type="email"
          value={draft}
          placeholder={placeholder || "Add an email and press Enter"}
          onChange={(e) => {
            setDraft(e.target.value);
            setLocalError("");
          }}
          onKeyDown={handleKeyDown}
          className={styles["helpdesk-config__chip-input"]}
        />
        <button
          type="button"
          className={styles["helpdesk-config__chip-add"]}
          onClick={addEmail}
          aria-label="Add email"
        >
          <Plus size={16} strokeWidth={2.5} />
        </button>
      </div>

      {(error || localError) && (
        <p className={styles["helpdesk-config__error-text"]}>
          {error || localError}
        </p>
      )}

      {value.length > 0 && (
        <div className={styles["helpdesk-config__chips"]}>
          {value.map((email, idx) => (
            <span
              key={`${email}-${idx}`}
              className={styles["helpdesk-config__chip"]}
            >
              <AtSign size={12} strokeWidth={2.5} />
              {email}
              <button
                type="button"
                className={styles["helpdesk-config__chip-remove"]}
                onClick={() => removeEmail(idx)}
                aria-label={`Remove ${email}`}
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const HelpDeskConfig = () => {
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [defaultSlaHours, setDefaultSlaHours] = useState("");
  const [notifyOrgOnStatusChange, setNotifyOrgOnStatusChange] = useState(true);

  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/help-desk/config");
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load config",
          }),
        );
      }
      const data = res.data || {};
      setConfig(data);
      setToEmails(Array.isArray(data.toEmails) ? data.toEmails : []);
      setCcEmails(Array.isArray(data.ccEmails) ? data.ccEmails : []);
      setBccEmails(Array.isArray(data.bccEmails) ? data.bccEmails : []);
      setDefaultSlaHours(
        data.defaultSlaHours === null || data.defaultSlaHours === undefined
          ? ""
          : String(data.defaultSlaHours),
      );
      setNotifyOrgOnStatusChange(
        typeof data.notifyOrgOnStatusChange === "boolean"
          ? data.notifyOrgOnStatusChange
          : true,
      );
    } catch (err) {
      showNotification(getErrorMessage(err, "Failed to load config"), "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const validate = () => {
    const next = {};
    if (toEmails.length === 0) {
      next.toEmails = "At least one TO email is required";
    }
    const slaNum = Number(defaultSlaHours);
    if (
      defaultSlaHours === "" ||
      Number.isNaN(slaNum) ||
      slaNum <= 0 ||
      !Number.isInteger(slaNum)
    ) {
      next.defaultSlaHours = "Enter a valid SLA in whole hours";
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const payload = {
        toEmails,
        ccEmails,
        bccEmails,
        defaultSlaHours: Number(defaultSlaHours),
        notifyOrgOnStatusChange,
      };
      const res = await apiPut("catalog", "/help-desk/config", payload);
      if (res?.status === "success") {
        showNotification(
          res?.message || "Config updated successfully",
          "success",
        );
        const data = res.data || {};
        setConfig(data);
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update config",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to update config"),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles["helpdesk-config__page"]}>
      {notification && (
        <div className={styles["helpdesk-config__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["helpdesk-config__header"]}>
        <Link to={BACK_PATH} className={styles["helpdesk-config__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["helpdesk-config__title-group"]}>
          <h2 className={styles["helpdesk-config__title"]}>
            Help Desk Configuration
          </h2>
          <p className={styles["helpdesk-config__subtitle"]}>
            Notification routing and default SLA used for every new ticket.
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles["helpdesk-config__loading"]}>
          <Loader2 className={styles["helpdesk-config__spin"]} size={28} />
          <span>Loading config…</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles["helpdesk-config__card"]}>
            <div className={styles["helpdesk-config__card-header"]}>
              <div className={styles["helpdesk-config__card-icon"]}>
                <AtSign size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-config__card-title"]}>
                Email Routing
              </h3>
            </div>

            <div className={styles["helpdesk-config__email-grid"]}>
              <EmailChipsField
                label="TO Emails"
                value={toEmails}
                onChange={(next) => {
                  setToEmails(next);
                  setErrors((prev) => ({ ...prev, toEmails: "" }));
                }}
                error={errors.toEmails}
                placeholder="support@company.com"
              />
              <EmailChipsField
                label="CC Emails"
                value={ccEmails}
                onChange={setCcEmails}
                placeholder="lead@company.com"
              />
              <EmailChipsField
                label="BCC Emails"
                value={bccEmails}
                onChange={setBccEmails}
                placeholder="audit@company.com"
              />
            </div>
          </div>

          <div className={styles["helpdesk-config__card"]}>
            <div className={styles["helpdesk-config__card-header"]}>
              <div className={styles["helpdesk-config__card-icon"]}>
                <Clock size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-config__card-title"]}>
                Default SLA
              </h3>
            </div>

            <div className={styles["helpdesk-config__sla-grid"]}>
              <Input
                label="Default SLA Hours"
                type="number"
                name="defaultSlaHours"
                placeholder="e.g. 8"
                value={defaultSlaHours}
                onChange={(e) => {
                  setDefaultSlaHours(e.target.value);
                  setErrors((prev) => ({ ...prev, defaultSlaHours: "" }));
                }}
                error={errors.defaultSlaHours}
                required
                min={1}
              />

              {config?.updatedAt && (
                <div className={styles["helpdesk-config__meta"]}>
                  <span>Last updated</span>
                  <strong>{formatDateTime(config.updatedAt)}</strong>
                </div>
              )}
            </div>
          </div>

          <div className={styles["helpdesk-config__card"]}>
            <div className={styles["helpdesk-config__card-header"]}>
              <div className={styles["helpdesk-config__card-icon"]}>
                <Bell size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-config__card-title"]}>
                Notifications
              </h3>
            </div>

            <label className={styles["helpdesk-config__checkbox-row"]}>
              <input
                type="checkbox"
                checked={notifyOrgOnStatusChange}
                onChange={(e) =>
                  setNotifyOrgOnStatusChange(e.target.checked)
                }
                className={styles["helpdesk-config__checkbox"]}
              />
              <span className={styles["helpdesk-config__checkbox-text"]}>
                <strong>
                  Notify organization users when a ticket status changes
                </strong>
                <small>
                  When enabled, an email is sent to the organization’s users
                  every time a ticket they raised moves to a new status.
                </small>
              </span>
            </label>
          </div>

          <div className={styles["helpdesk-config__actions"]}>
            <button
              type="button"
              className={`${styles["helpdesk-config__btn"]} ${styles["helpdesk-config__btn--secondary"]}`}
              onClick={() => navigate(BACK_PATH)}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles["helpdesk-config__btn"]} ${styles["helpdesk-config__btn--primary"]}`}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting ? "Saving…" : "Save Config"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default HelpDeskConfig;
